import {LngLat, LngLatBounds, Point, PointLike, Marker as MarkerInstance} from "maplibre-gl";
import React, {RefObject} from "react";
import {Marker, MarkerProps, MarkerDragEvent, MarkerEvent} from "react-map-gl/maplibre";
import MapMarkerIcon, {IMapMarkerIconProps, MapMarkerIconInstance} from "./MapMarkerIcon";
import {MapLibreMapInstance} from "../MapLibreMap";
import {Property} from "csstype";
import {IMapPopupProps} from "./MapPopup";
import MapMarkerImageIcon, {MapMarkerImageIconInstance} from "./MapMarkerImageIcon";
import {mapCoreConfig} from "../config/MapCoreConfig";
import BasicComponent from "../../../tsx/component/BasicComponent";
import {requirePoint} from "../utils/MapUtils";


export type MarkerType = "cluster" | "primary_marker" | "user" | "custom";

export interface IMarkerProps
{
    marker:MarkerProps;
    markerID?:string|null;
    bounds?:LngLatBounds
    outOfBounds?:boolean
    preventPropsPositionUpdate?: boolean;
    icon?: React.ReactNode;
    iconProps?:IMapMarkerIconProps
    iconRef?:RefObject<MapMarkerIconInstance|null>
    popupProps?:IMapPopupProps
    centreOnClick?:boolean
    focusOffset?:PointLike
    objectIDs?:string[]
    popupContent?: React.ReactNode|((marker:MapMarkerInstance) => React.ReactNode);
    defaultZIndex?:Property.ZIndex;
    defaultZoomLevel?:number|null;
    fallbackToBoundsZoomLevel?:boolean;
}

export interface IMarkersProps
{
    [key: string]: IMarkerProps;
}

interface IProps extends IMarkerProps
{
    map:MapLibreMapInstance
    markerType:MarkerType
}

export class MapMarkerInstance extends BasicComponent<IProps>
{

    props: IProps;

    popupProps:IMapPopupProps|null = null;
    focusOffset:Point|null = null;
    popupContent: React.ReactNode|((marker:MapMarkerInstance) => React.ReactNode)|null = null;

    onDragEnd: ((e: MarkerDragEvent) => void)|null = null;
    onClick: ((e: MarkerEvent<MouseEvent>) => void)|null = null;

    iconRef:RefObject<MapMarkerIconInstance|MapMarkerImageIconInstance|null>|null = null;
    markerProps:MarkerProps = null!;
    markerRef = React.createRef<MarkerInstance>();
    positionWillRender:boolean = false;
    selectedObjectID:string|null = null;
    selectedMarkerZIndex = 6;
    markerID:string|null = null;
    markerType:MarkerType = null!;
    centreOnClick: boolean = false;
    defaultZoomLevel:number|null = null;
    iconTransform: Property.Transform|null = null;

    constructor(props: IProps)
    {
        super(props);
        this.props = props;
    }

    bindFunctions()
    {
        super.bindFunctions();
        this.handleDragEnd = this.handleDragEnd.bind(this);
        this.handleOnClick = this.handleOnClick.bind(this);
    }

    applyNextProps(nextProps:IProps, props?:IProps)
    {
        super.applyNextProps(nextProps, props);

        this.popupProps = nextProps.popupProps ?? null;
        this.popupContent = nextProps.popupContent ?? null;

        this.markerID = nextProps.markerID ?? null;
        this.markerType = nextProps.markerType;
        this.defaultZoomLevel = nextProps.defaultZoomLevel ?? null;

        let focusOffset:Point|null = null;
        let iconRef:RefObject<MapMarkerIconInstance|null>|null = this.iconRef;
        let markerProps:MarkerProps|null = null;
        let defaultZIndex:Property.ZIndex|undefined = nextProps.defaultZIndex ?? nextProps.marker.style?.zIndex;
        let currentZIndex:Property.ZIndex|undefined = nextProps.map.selectedMarker !== this ? defaultZIndex : this.selectedMarkerZIndex;

        if(props){
            focusOffset = props.focusOffset === nextProps.focusOffset ? this.focusOffset : (props.focusOffset ? requirePoint(props.focusOffset) : null);

            markerProps = props.marker === nextProps.marker ? this.markerProps : null;
            if(markerProps && (markerProps.style?.zIndex !== currentZIndex)){
                markerProps = null;
            }
            if((props.iconRef !== nextProps.iconRef) || (props.icon !== nextProps.icon)){
                iconRef = null;
            }
        }

        if(!iconRef){
            iconRef = nextProps.iconRef ?? (!nextProps.icon ? React.createRef<MapMarkerIconInstance>() : null);
        }

        if(!markerProps){
            let nextMarkerProps:MarkerProps = nextProps.marker;
            const currentLngLat = this.getLngLat();

            if(nextProps.preventPropsPositionUpdate){
                if(currentLngLat && (nextMarkerProps.latitude !== currentLngLat.lat || nextMarkerProps.longitude !== currentLngLat.lng)){
                    nextMarkerProps = {
                        ...nextMarkerProps,
                        latitude: currentLngLat.lat,
                        longitude: currentLngLat.lng
                    };
                }
            }
            if(!currentLngLat || (nextMarkerProps.latitude !== currentLngLat.lat || nextMarkerProps.longitude !== currentLngLat.lng)){
                this.positionWillRender = true;
            }
            markerProps = nextMarkerProps.style?.zIndex === currentZIndex ? nextMarkerProps : {
                ...nextMarkerProps,
                anchor: "top-left",
                style: {
                    ...nextMarkerProps.style,
                    zIndex: currentZIndex
                }
            };
        }
        this.determineIconTransform();
        this.centreOnClick = nextProps.centreOnClick ?? false;

        this.markerProps = markerProps
        this.focusOffset = focusOffset;
        this.iconRef = iconRef;
        this.onDragEnd = !!nextProps.marker.draggable ? this.handleDragEnd : (nextProps.marker.onDragEnd ?? null);
        this.onClick = this.handleOnClick;
    }


    determineIconTransform()
    {
        const anchor = this.props.marker?.anchor ?? 'center';
        let adjustLeft = 0
        let adjustTop = 0
        if((anchor === "center") || (anchor === "top") || (anchor === "bottom")){
            adjustLeft = -50;
        } else if((anchor === "top-right") || (anchor === "right") || (anchor === "bottom-right")){
            adjustLeft = -100;
        }
        if((anchor === "left") || (anchor === "center") || (anchor === "right")){
            adjustTop = -50;
        } else if((anchor === "bottom-left") || (anchor === "bottom") || (anchor === "bottom-right")){
            adjustTop = -100;
        }
        if(adjustLeft || adjustTop){
            this.iconTransform = 'translate(' + adjustLeft +'%, ' + adjustTop + '%)';
        } else {
            this.iconTransform = null;
        }
    }

    appliedBounds(): LngLatBounds|null{
        return this.props.bounds ?? null;
    }

    handleDragEnd = (event: MarkerDragEvent) => {
        if(!this.props.map.mounted){
            return;
        }
        this.props.map.onMarkerPositionChange(this);
        if(this.markerProps.onDragEnd){
            this.markerProps.onDragEnd(event);
        }
    }

    handleOnClick = (event:MarkerEvent<MouseEvent>) => {
        (event.originalEvent as any).clickOnLayer = true

        if(!this.props.map.mounted){
            return;
        }
        const map = this.props.map;
        if(this.props.popupContent){
            if(map.selectedMarker !== this || !map.showSelectedMarkerPopup){
                map.selectMarker(this, this.centreOnClick, true)
            } else {
                map.closePopup();
            }
        } else if(this.centreOnClick){
            map.selectMarker(this, true)
        }
        if (this.markerProps.onClick)
        {
            this.markerProps.onClick(event);
        }
        this.props.map.refreshState();
    }

    onStateEstablished()
    {
        super.onStateEstablished();
        if(!this.props.map.mounted){
            return;
        }
        if(this.positionWillRender){
            this.positionWillRender = false;
            this.props.map.onMarkerPositionChange(this);
        }
    }

    getIcon(): MapMarkerIconInstance | null
    {
        return this.iconRef?.current ?? null;
    }

    getMarker(): MarkerInstance|null
    {
        return this.markerRef.current;
    }

    isAtDefaultPosition(): boolean
    {
        const latLn = this.getLngLat();
        return !latLn || ((latLn.lat === this.props.marker.latitude) && (latLn.lng === this.props.marker.longitude))
    }

    getLngLat(): LngLat|null
    {
        const marker  = this.getMarker();
        return marker?.getLngLat() ?? null;
    }

    getSelectedObjectID(): string|null
    {
        if(this.selectedObjectID && this.props.objectIDs?.includes(this.selectedObjectID)){
            return this.selectedObjectID;
        }
        if((this.props.objectIDs ?? []).length){
            return this.props.objectIDs![0];
        }
        return null;
    }

    onPopupClose(): void
    {
        this.selectedObjectID = null;
    }


    componentDidFirstMount()
    {
        super.componentDidFirstMount();
        const enableContextMenu = this.props.map.props.enableContextMenu ?? (mapCoreConfig.enableContextMenu ?? false);
        if(enableContextMenu){

            this.getMarker()?._element?.addEventListener("contextmenu", (event) => {
                event.stopImmediatePropagation();
            })
        }
    }

    renderComponent()
    {
        return <Marker
            {...this.markerProps}
            ref={this.markerRef}
            onClick={(this.onClick) ?? undefined}
            onDragEnd={this.onDragEnd ?? undefined}
        >
            <div>
                <div style={{transform: this.iconTransform ?? undefined}}>
                    {this.props.icon ?? <>
                        {!this.props.iconProps?.imagePath ?
                            <MapMarkerIcon
                                {...this.props.iconProps}
                                ref={this.iconRef}
                            /> : <MapMarkerImageIcon
                                {...this.props.iconProps}
                                ref={this.iconRef as RefObject<MapMarkerImageIconInstance|null>}
                            />}
                    </>}
                </div>
            </div>
        </Marker>
    }
}

const MapMarker  = React.memo(MapMarkerInstance);
export default MapMarker;